/**
 * Phase PWA — Service worker registration + version polling.
 *
 * Strategy:
 *   1. Fetch the current SW version tag from the backend.
 *   2. Register `/sw.js?v=<tag>` with scope `/`.
 *   3. Poll the version endpoint every 5 minutes. If the tag changed,
 *      re-register (URL changes → browser fetches new SW → `installed`
 *      worker becomes waiting → we postMessage `SKIP_WAITING` → reload).
 *   4. On `appinstalled` event, persist `pwa_installed=true` so the
 *      install prompt never shows again, and POST to the install-count
 *      endpoint once.
 *
 * Bundled into the main app entry — registered only after the React
 * tree has rendered to avoid competing with the first paint.
 */

const VERSION_ENDPOINT = "/api/public/pwa/sw-version";
const INSTALL_COUNT_ENDPOINT = "/api/public/pwa/installs";
const POLL_INTERVAL_MS = 5 * 60 * 1000;
const LS_INSTALLED_KEY = "rp_pwa_installed";
const LS_INSTALL_REPORTED_KEY = "rp_pwa_install_reported";

let _currentVersionTag = null;
let _registration = null;

function _backendBase() {
    // Phase APEX-C — same-origin only. The SW must be on the same origin
    // as the page anyway, and the version endpoint is served from the same
    // ingress. Reading the env var in production builds caused a 3-incident
    // outage chain (apex/www CORS preflight 308). Production hardcoded to
    // relative; dev workflow can still override via REACT_APP_BACKEND_URL.
    if (process.env.NODE_ENV === "development") {
        return (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
    }
    return "";
}

async function _fetchVersion() {
    try {
        const res = await fetch(`${_backendBase()}${VERSION_ENDPOINT}`, {
            cache: "no-store",
        });
        if (!res.ok) return null;
        const data = await res.json();
        return Number(data.sw_version_tag) || 1;
    } catch {
        return null;
    }
}

async function _reportInstalled() {
    if (window.localStorage.getItem(LS_INSTALL_REPORTED_KEY) === "1") return;
    try {
        await fetch(`${_backendBase()}${INSTALL_COUNT_ENDPOINT}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({}),
        });
        window.localStorage.setItem(LS_INSTALL_REPORTED_KEY, "1");
    } catch {
        /* best-effort; tolerate failure */
    }
}

async function _doRegister(versionTag) {
    if (!("serviceWorker" in navigator)) return null;
    try {
        const reg = await navigator.serviceWorker.register(
            `/sw.js?v=${versionTag}`,
            { scope: "/" },
        );
        _registration = reg;
        // If a new SW is already waiting from a previous tab, ping it.
        if (reg.waiting) reg.waiting.postMessage({ type: "SKIP_WAITING" });
        reg.addEventListener("updatefound", () => {
            const installing = reg.installing;
            if (!installing) return;
            installing.addEventListener("statechange", () => {
                if (installing.state === "installed" && navigator.serviceWorker.controller) {
                    // New SW activated while we have an old controller —
                    // tell it to skip waiting and reload after activation.
                    installing.postMessage({ type: "SKIP_WAITING" });
                }
            });
        });
        return reg;
    } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("PWA register failed:", e);
        return null;
    }
}

let _reloadPending = false;
function _attachControllerReload() {
    if (!("serviceWorker" in navigator)) return;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
        if (_reloadPending) return;
        _reloadPending = true;
        // Small debounce: let in-flight requests settle before reload.
        setTimeout(() => window.location.reload(), 300);
    });
}

async function _pollAndMaybeUpgrade() {
    const tag = await _fetchVersion();
    if (!tag) return;
    if (_currentVersionTag === null) {
        _currentVersionTag = tag;
        await _doRegister(tag);
        return;
    }
    if (tag !== _currentVersionTag) {
        _currentVersionTag = tag;
        // Re-register with new ?v= — browser fetches new SW bytes.
        await _doRegister(tag);
    } else if (_registration) {
        // Same tag — still call update() periodically so browsers that
        // ignore Service-Worker-Allowed semantics check for byte changes.
        try { await _registration.update(); } catch { /* ignore */ }
    }
}

export function registerPWA() {
    if (typeof window === "undefined") return;
    if (!("serviceWorker" in navigator)) return;

    // Reload on controller change (= new SW took control).
    _attachControllerReload();

    // Listen for `appinstalled` once globally; persist + report.
    window.addEventListener("appinstalled", () => {
        try { window.localStorage.setItem(LS_INSTALLED_KEY, "1"); } catch { /* ignore */ }
        _reportInstalled();
    });

    // Kick off registration + start polling.
    _pollAndMaybeUpgrade();
    setInterval(_pollAndMaybeUpgrade, POLL_INTERVAL_MS);

    // Wire the SW-side RUN_SYNC trigger to the queue flush.
    navigator.serviceWorker.addEventListener("message", (event) => {
        if (event.data && event.data.type === "RUN_SYNC") {
            import("./sync-engine").then((mod) => mod.flushInspectionQueue());
        }
    });
}

export function isAlreadyInstalled() {
    try { return window.localStorage.getItem(LS_INSTALLED_KEY) === "1"; }
    catch { return false; }
}
